import { useState } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { parseCsvData } from "@/utils/parseCsvData";
import { LoadingButton } from "@/components/ui/loading-button";
import { Promotion } from "@/constants/objects";
import { useAppSelector, useAppDispatch } from "@/hooks/reducer-hooks";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Upload, Info } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { chunkArray, postCsvData, removeResidents } from "@/services/csvService";
import { Button } from "@/components/ui/button";
import { SelectSubscriptionPromotion } from "@/components/SelectSubscriptionPromotion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getSubscriptionPromotions } from "@/services/userDataService";
import { setSubscriptionPromotions } from "@/slices/userSlice";

export function CSVUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string[][]>([]);
  const [importOption, setImportOption] = useState("add");
  const [warning, setWarning] = useState("");
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
  const [uploading, setUploading] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const user = useAppSelector((state) => state.user);
  const promotions = user.subscriptionPromotions || [];
  const CHUNK_SIZE = 100;

  const onDrop = (acceptedFiles: File[]) => {
    const csvFile = acceptedFiles[0];
    if (csvFile && csvFile.type === "text/csv") {
      // If size is greater than 5MB then show error
      if (csvFile.size > 5 * 1024 * 1024) {
        toast({
          title: "File size too large",
          description: "Please upload a file smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }

      Papa.parse(csvFile, {
        complete: (results) => {
          try {
            const data = results.data as string[][];
            // Call the parser/validator
            const { validEmails, duplicates } = parseCsvData(data);
            setEmails(validEmails);
            // Handle duplicates if needed
            if (duplicates.length > 0) {
              setWarning(`Found duplicates: ${duplicates.length}`);
              // You can show a toast or handle them in any specific way you prefer.
            }
            setFile(csvFile);
            // Show preview (first 6 rows)
            setPreview(results.data.slice(0, 6) as string[][]);
          } catch (error: any) {
            toast({
              title: "CSV Parsing Error",
              description: error.message,
              variant: "destructive",
            });
            setFile(null);
          }
        },
      });
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV file.",
        variant: "destructive",
      });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "text/csv": [".csv"] },
  });

  const handleUpload = async () => {
    if (!file || !selectedPromotion) return;

    // Check if voucher has enough remaining slots (only for add/replace operations)
    if (
      importOption !== "remove" &&
      selectedPromotion?.type === "voucher" &&
      emails.length > selectedPromotion.amountRemaining
    ) {
      toast({
        title: "Upload failed",
        description: `This voucher only has ${selectedPromotion.amountRemaining} slots remaining. Your CSV contains ${emails.length} emails.`,
        variant: "destructive",
      });
      return;
    }

    try {
      setUploading(true);
      setProgress(0);

      if (importOption === "remove") {
        // Handle remove operation
        const emailChunks = chunkArray(emails, CHUNK_SIZE);
        const totalChunks = emailChunks.length;

        for (let i = 0; i < emailChunks.length; i++) {
          const emailChunk = emailChunks[i];

          await removeResidents({
            emails: emailChunk,
            subscriptionPromotionLogicId: selectedPromotion.id,
          });

          setProgress(((i + 1) / totalChunks) * 100);
        }

        toast({
          title: "Remove successful",
          description: `${emails.length} residents have been removed from the list.`,
          variant: "success",
        });
      } else {
        // Handle add/replace operations
        const emailChunks = chunkArray(emails, CHUNK_SIZE);
        const totalChunks = emailChunks.length;

        for (let i = 0; i < emailChunks.length; i++) {
          const emailChunk = emailChunks[i];

          await postCsvData({
            emails: emailChunk,
            importOption,
            subscriptionPromotionLogicId: selectedPromotion.id,
          });

          setProgress(((i + 1) / totalChunks) * 100);
        }

        toast({
          title: "Upload successful",
          description: `${file.name} has been ${
            importOption === "add" ? "added to" : "replaced in"
          } the resident list.`,
          variant: "success",
        });

        // Refresh promotions data after successful upload
        const updatedPromotions = await getSubscriptionPromotions();
        // You'll need to implement this using your state management solution (Redux)
        // dispatch(setSubscriptionPromotions(updatedPromotions));
      }
    } catch (error: any) {
      toast({
        title: importOption === "remove" ? "Remove failed" : "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setFile(null);
      setPreview([]);
      setUploading(false);
      setSelectedPromotion(null);
      setProgress(0);
      setImportOption("add");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Residents Data</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-md p-6 text-center flex flex-col items-center justify-center ${
            isDragActive ? "border-primary" : "border-muted"
          } min-h-40`}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Drop the CSV file here...</p>
          ) : (
            <p>Drag and drop a CSV file here, or click to select a file</p>
          )}
          <Button variant={"outline"} className="mt-4 hover:bg-primary hover:text-white">
            <Upload />
            upload
          </Button>
        </div>
        {file && (
          <div className="mt-4">
            <p>
              File: {file.name} (
              {file.size / 1024 > 1024
                ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                : `${(file.size / 1024).toFixed(2)} KB`}
              )
            </p>

            <div className="mt-4">{warning && <p className="text-red-600">{warning}</p>}</div>

            <Alert className="my-4">
              <Info className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                To apply the voucher, you need to use the email associated with resident's current
                TULU account. If they do not yet have a TULU account, please use the email they plan
                to sign up with.
              </AlertDescription>
            </Alert>

            {importOption !== "remove" && selectedPromotion?.isVoucher && (
              <>
                <div className="bg-muted p-4 rounded-lg space-y-2 my-4">
                  <h4 className="font-medium">How it works:</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm">
                    <li>
                      If a resident already has an active subscription, the free months will be
                      added on top of their current plan.
                    </li>
                    <li>
                      Once the resident signs up for TULU using the email linked to the voucher, the
                      free months will be automatically applied.
                    </li>
                    <li>
                      The benefits remain the same for both 3-month and 6-month vouchers:
                      <ul className="list-disc pl-4 mt-1">
                        <li>Free unlimited rentals for set hour</li>
                        <li>Access to shop with year round promotions, benefits and flash sales</li>
                        <li>Access to print</li>
                      </ul>
                    </li>
                  </ul>
                </div>
              </>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  {preview[0]?.map((header: string, index: number) => (
                    <TableHead key={index}>{header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.slice(1).map((row: string[], rowIndex: number) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell: string, cellIndex: number) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center space-x-4 mt-4">
              <RadioGroup className="mt-4" value={importOption} onValueChange={setImportOption}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="add" />
                  <Label htmlFor="add">Add to Existing List</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="replace" id="replace" />
                  <Label htmlFor="replace">Replace Existing List</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="remove" id="remove" />
                  <Label htmlFor="remove">Remove Residents from Existing List</Label>
                </div>
              </RadioGroup>
              <SelectSubscriptionPromotion
                promotions={promotions}
                setSelectedPromotion={setSelectedPromotion}
                selectedPromotion={selectedPromotion}
              />
            </div>

            {importOption !== "remove" &&
              selectedPromotion?.type === "voucher" &&
              emails.length > 0 && (
                <div className="mt-4 p-4 border rounded">
                  <h4 className="font-medium mb-2">Voucher Status</h4>
                  <div className="space-y-1 text-sm">
                    <p>Emails in CSV: {emails.length}</p>
                    <p>Voucher remaining: {selectedPromotion.amountRemaining}</p>
                    {emails.length > selectedPromotion.amountRemaining && (
                      <p className="text-destructive font-medium">
                        Warning: Your CSV contains more emails than available voucher slots
                      </p>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col items-start w-full">
        {file && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <LoadingButton
                disabled={
                  !file ||
                  !selectedPromotion ||
                  (importOption !== "remove" &&
                    selectedPromotion?.type === "voucher" &&
                    emails.length > selectedPromotion.amountRemaining)
                }
                loading={uploading}
              >
                {uploading
                  ? importOption === "remove"
                    ? "Removing..."
                    : "Uploading..."
                  : importOption === "remove"
                  ? "Remove Residents"
                  : "Upload File"}
              </LoadingButton>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  {importOption === "add"
                    ? "You are about to add residents to the promotion list."
                    : importOption === "replace"
                    ? "You are about to replace the existing residents."
                    : "You are about to remove residents from the existing list."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    toast({
                      title: "Upload cancelled",
                      description: "You have cancelled the upload.",
                      variant: "default",
                    });
                  }}
                >
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction onClick={handleUpload}>Continue</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {uploading && emails.length > 100 && (
          <div className="w-full mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-center text-sm mt-2">{Math.round(progress)}% Uploaded</p>
          </div>
        )}
      </CardFooter>
    </Card>
  );
}
